'use client';

import React from 'react';
import { GlobalErrorBoundary } from '@/components/global-error-boundary';
import { usePathname } from 'next/navigation';

interface ErrorBoundaryProviderProps {
  children: React.ReactNode;
}

// Context for error handling across the app
interface ErrorContextValue {
  captureError: (error: Error, errorInfo?: React.ErrorInfo) => void;
  clearError: () => void;
}

const ErrorContext = React.createContext<ErrorContextValue | null>(null);

export function ErrorBoundaryProvider({
  children
}: ErrorBoundaryProviderProps) {
  const pathname = usePathname();
  const [capturedError, setCapturedError] = React.useState<Error | null>(null);

  // Reset error state on navigation (setState in render under an inequality
  // guard — recommended React pattern for resetting state from props).
  const [prevPathname, setPrevPathname] = React.useState(pathname);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    if (capturedError) setCapturedError(null);
  }

  const captureError = React.useCallback(
    (error: Error, errorInfo?: React.ErrorInfo) => {
      console.error('Error captured:', { error, errorInfo });
      setCapturedError(error);

      // TODO: Add error tracking service integration here
      // Example: Sentry.captureException(error, { extra: errorInfo });
    },
    []
  );

  const clearError = React.useCallback(() => {
    setCapturedError(null);
  }, []);

  // Throw captured error to be caught by error boundary
  React.useEffect(() => {
    if (capturedError) {
      throw capturedError;
    }
  }, [capturedError]);

  const contextValue = React.useMemo(
    () => ({ captureError, clearError }),
    [captureError, clearError]
  );

  return (
    <ErrorContext value={contextValue}>
      <GlobalErrorBoundary>{children}</GlobalErrorBoundary>
    </ErrorContext>
  );
}

// Hook to use error handling in components
export function useErrorHandler() {
  const context = React.use(ErrorContext);

  if (!context) {
    throw new Error(
      'useErrorHandler must be used within ErrorBoundaryProvider'
    );
  }

  return context;
}

// Async error wrapper hook
export function useAsyncError() {
  const { captureError } = useErrorHandler();

  return React.useCallback(
    (error: Error) => {
      captureError(error);
    },
    [captureError]
  );
}
